import { Analytics } from '@easy-expense/analytics-client';
import { currentUser } from '@easy-expense/auth-client';
import { useCachedMembers, useIsAdminOrManagerRole } from '@easy-expense/data-firestore-client';
import {
  ApprovalStatus,
  Expense,
  Rejection,
  ApprovalSource,
  ReimbursementSource,
} from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { dateFormatSkeleton, getUserDisplayName } from '@easy-expense/utils-shared';
import React from 'react';

import { Button } from '../Button.components';

const MAX_REJECTION_NOTE_LENGTH = 500;

type ApprovalPatch = Partial<
  Pick<
    Expense,
    'approvalStatus' | 'rejection' | 'approvalSource' | 'reimbursementSource' | 'reimbursableState'
  >
>;

function approveExpense(expenseKey: string, onUpdate: (patch: ApprovalPatch) => void) {
  const approvedBy = currentUser()?.uid;
  if (!approvedBy) {
    return;
  }
  const patch: ApprovalPatch = {
    approvalStatus: 'approved',
    approvalSource: { approvedBy, approvedAt: Date.now() },
  };
  Data.expenses.update(expenseKey, patch, undefined, undefined, 'approval');
  onUpdate(patch);
  Analytics.track('expense_approved', { expenseKey, approvedBy });
}

function rejectExpense(
  expenseKey: string,
  reason: string,
  onUpdate: (patch: ApprovalPatch) => void,
) {
  const rejectedBy = currentUser()?.uid;
  if (!rejectedBy) {
    return;
  }
  const patch: ApprovalPatch = {
    approvalStatus: 'rejected',
    rejection: { rejectedBy, reason, rejectedAt: Date.now() },
  };
  Data.expenses.update(expenseKey, patch, undefined, undefined, 'approval');
  onUpdate(patch);
  Analytics.track('expense_rejected', { expenseKey, rejectedBy });
}

function undoExpenseApproval(expenseKey: string, onUpdate: (patch: ApprovalPatch) => void) {
  const undoneBy = currentUser()?.uid;
  if (!undoneBy) {
    return;
  }
  const patch: ApprovalPatch = { approvalStatus: 'pending', approvalSource: null };
  Data.expenses.update(expenseKey, patch, undefined, undefined, 'approval');
  onUpdate(patch);
  Analytics.track('expense_approval_undone', { expenseKey, undoneBy });
}

export function undoExpenseRejection(
  expenseKey: string,
  onUpdate: (patch: ApprovalPatch) => void = () => {},
) {
  const undoneBy = currentUser()?.uid;
  if (!undoneBy) {
    return;
  }
  const patch: ApprovalPatch = { approvalStatus: 'pending', rejection: null };
  Data.expenses.update(expenseKey, patch, undefined, undefined, 'approval');
  onUpdate(patch);
  Analytics.track('expense_rejection_undone', { expenseKey, undoneBy });
}

export function resubmitExpense(
  expenseKey: string,
  onUpdate: (patch: ApprovalPatch) => void = () => {},
) {
  const resubmittedBy = currentUser()?.uid;
  if (!resubmittedBy) {
    return;
  }
  const patch: ApprovalPatch = { approvalStatus: 'pending', rejection: null };
  Data.expenses.update(expenseKey, patch, undefined, undefined, 'approval');
  onUpdate(patch);
  Analytics.track('expense_resubmitted', { expenseKey, resubmittedBy });
}

function reimburseExpense(expenseKey: string, onUpdate: (patch: ApprovalPatch) => void) {
  const reimbursedBy = currentUser()?.uid;
  if (!reimbursedBy) {
    return;
  }
  const patch: ApprovalPatch = {
    reimbursableState: 'reimbursed',
    reimbursementSource: { reimbursedBy, reimbursedAt: Date.now() },
  };
  Data.expenses.update(expenseKey, patch, undefined, undefined, 'approval');
  onUpdate(patch);
  Analytics.track('expense_reimbursed', { expenseKey, reimbursedBy });
}

function undoExpenseReimbursement(expenseKey: string, onUpdate: (patch: ApprovalPatch) => void) {
  const undoneBy = currentUser()?.uid;
  if (!undoneBy) {
    return;
  }
  const patch: ApprovalPatch = { reimbursableState: 'notReimbursed', reimbursementSource: null };
  Data.expenses.update(expenseKey, patch, undefined, undefined, 'approval');
  onUpdate(patch);
  Analytics.track('expense_reimbursement_undone', { expenseKey, undoneBy });
}

export const ApprovalStatusSection: React.FC<{
  approvalStatus?: ApprovalStatus;
  rejection?: Rejection | null;
  approvalSource?: ApprovalSource | null;
  reimbursementSource?: ReimbursementSource | null;
  isReimbursable?: boolean | null;
  reimbursableState?: 'reimbursed' | 'notReimbursed' | null;
  total?: number | null;
  createdBy?: string;
  paymentMethod?: string | null;
  expenseKey: string;
  onUpdate?: (patch: ApprovalPatch) => void;
  setLoading?: (loading: boolean) => void;
}> = ({
  approvalStatus,
  rejection,
  approvalSource,
  reimbursementSource,
  isReimbursable,
  reimbursableState,
  total,
  createdBy,
  paymentMethod,
  expenseKey,
  onUpdate = () => {},
  setLoading = () => {},
}) => {
  const isManagerOrAdmin = useIsAdminOrManagerRole();
  const members = useCachedMembers();
  const paymentMethodValue = Data.paymentMethods.useByKey(paymentMethod ?? '');
  const { formatCurrency, formatDate } = useIntlStore();
  const [showRejectInput, setShowRejectInput] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState('');
  const needsReimbursement =
    approvalStatus === 'approved' && isReimbursable && reimbursableState !== 'reimbursed';
  const hasBeenReimbursed =
    approvalStatus === 'approved' && isReimbursable && reimbursableState === 'reimbursed';

  if (!approvalStatus) {
    return null;
  }

  function memberName(userID?: string) {
    return getUserDisplayName(members.find((m) => m.key === userID));
  }

  function formatShortDate(millis: number) {
    return formatDate(new Date(millis), { skeleton: dateFormatSkeleton.abbreviated });
  }

  function runAction(action: (onUpdate: (patch: ApprovalPatch) => void) => void) {
    setLoading(true);
    try {
      action(onUpdate);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout.Column>
      {approvalStatus === 'pending' ? (
        <Layout.Column>
          <Layout.Row
            align
            justify="center"
            px
            py={16}
            bg="warningXLight"
            border={[2, 'solid', 'warning']}
            radius
            style={{ width: '100%' }}
          >
            <Icon name="time-outline" size={20} color={theme.colors.warning} />
            <Spacer.Horizontal size={8} />
            <OpenSans.Primary weight="bold-700" style={{ color: theme.colors.warningDark }}>
              {getTranslation('Pending Approval')}
            </OpenSans.Primary>
          </Layout.Row>

          {isManagerOrAdmin && !showRejectInput ? (
            <>
              <Spacer.Vertical size={12} />
              <Layout.Row>
                <Button.DestructiveOutline
                  variant="bold"
                  py={12}
                  align="center"
                  content={getTranslation('Reject')}
                  style={{ flex: 1 }}
                  onClick={() => setShowRejectInput(true)}
                />
                <Spacer.Horizontal size={12} />
                <Button.Success
                  variant="bold"
                  py={12}
                  align="center"
                  style={{ flex: 3 }}
                  onClick={() => runAction((cb) => approveExpense(expenseKey, cb))}
                >
                  <Icon name="checkmark-outline" size={18} color={theme.colors.buttonWhite} />
                  <Spacer.Horizontal size={6} />
                  <OpenSans.Custom
                    size="m-18"
                    weight="bold-700"
                    style={{ color: theme.colors.buttonWhite }}
                  >
                    {getTranslation('Approve')}
                  </OpenSans.Custom>
                </Button.Success>
              </Layout.Row>
            </>
          ) : null}

          {isManagerOrAdmin && showRejectInput ? (
            <>
              <Spacer.Vertical size={12} />
              <OpenSans.Custom size="xs-12" weight="bold-700" color="destructiveDark">
                {getTranslation('Reason for rejection')}
              </OpenSans.Custom>
              <Spacer.Vertical size={6} />
              <Layout.Row
                border={[1, 'solid', 'destructive']}
                radius={4}
                bg="destructiveXLight"
                px
                py={8}
                style={{ minHeight: 80 }}
              >
                <textarea
                  name="rejectReason"
                  value={rejectReason}
                  placeholder={getTranslation('Reason for rejection')}
                  rows={3}
                  autoFocus
                  maxLength={MAX_REJECTION_NOTE_LENGTH}
                  style={{
                    flexGrow: 1,
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    resize: 'vertical',
                    backgroundColor: 'transparent',
                    fontFamily: 'inherit',
                    fontSize: 14,
                  }}
                  onChange={(event) =>
                    setRejectReason(event.currentTarget.value.slice(0, MAX_REJECTION_NOTE_LENGTH))
                  }
                />
              </Layout.Row>
              {rejectReason.length >= MAX_REJECTION_NOTE_LENGTH ? (
                <>
                  <Spacer.Vertical size={6} />
                  <OpenSans.Custom size="xs-12" color="destructiveDark">
                    {getTranslation('You have reached the 500 character limit.')}
                  </OpenSans.Custom>
                </>
              ) : null}
              <Spacer.Vertical size={12} />
              <Layout.Row>
                <Button.Outline
                  variant="bold"
                  align="center"
                  content={getTranslation('Cancel')}
                  style={{ flex: 1 }}
                  onClick={() => {
                    setShowRejectInput(false);
                    setRejectReason('');
                  }}
                />
                <Spacer.Horizontal size={12} />
                <Button.DestructiveSolid
                  variant="bold"
                  align="center"
                  active={rejectReason.length < MAX_REJECTION_NOTE_LENGTH}
                  style={{ flex: 3 }}
                  onClick={() => {
                    if (rejectReason.trim().length === 0) {
                      alert(getTranslation('Please add a reason for the rejection.'));
                      return;
                    }
                    runAction((cb) => {
                      rejectExpense(expenseKey, rejectReason.trim(), cb);
                      setShowRejectInput(false);
                      setRejectReason('');
                    });
                  }}
                >
                  <Icon name="send-outline" size={18} color={theme.colors.buttonWhite} />
                  <Spacer.Horizontal size={6} />
                  <OpenSans.Custom
                    size="m-18"
                    weight="bold-700"
                    style={{ color: theme.colors.buttonWhite }}
                  >
                    {getTranslation('Submit Rejection')}
                  </OpenSans.Custom>
                </Button.DestructiveSolid>
              </Layout.Row>
            </>
          ) : null}
        </Layout.Column>
      ) : null}

      {approvalStatus === 'rejected' ? (
        <Layout.Column px py bg="destructiveXLight" border={[2, 'solid', 'destructive']} radius>
          <Layout.Row align>
            <Icon name="close-circle" size={20} color={theme.colors.destructive} />
            <Spacer.Horizontal size={8} />
            <OpenSans.Primary weight="bold-700" style={{ color: theme.colors.destructiveDark }}>
              {getTranslation('Rejected')}
            </OpenSans.Primary>
          </Layout.Row>
          <Spacer.Vertical size={4} />
          <OpenSans.Secondary size="xs-12">
            {rejection
              ? `${getTranslation('Rejected by')} ${memberName(rejection.rejectedBy)}${
                  rejection.rejectedAt
                    ? ` ${getTranslation('on')} ${formatShortDate(rejection.rejectedAt)}`
                    : ''
                }${rejection.reason ? ` — "${rejection.reason}"` : ''}`
              : null}
          </OpenSans.Secondary>
        </Layout.Column>
      ) : null}

      {approvalStatus === 'rejected' && !isManagerOrAdmin ? (
        <>
          <Spacer.Vertical size={8} />
          <Layout.PressableRow
            bg="transparent"
            onClick={() => runAction((cb) => resubmitExpense(expenseKey, cb))}
          >
            <Icon name="arrow-undo-outline" size={16} color={theme.colors.brandPrimary} />
            <Spacer.Horizontal size={4} />
            <OpenSans.Pressable size="s-16">{getTranslation('Resubmit')}</OpenSans.Pressable>
          </Layout.PressableRow>
        </>
      ) : null}

      {approvalStatus === 'rejected' && isManagerOrAdmin ? (
        <>
          <Spacer.Vertical size={8} />
          <Layout.PressableRow
            bg="transparent"
            onClick={() => runAction((cb) => undoExpenseRejection(expenseKey, cb))}
          >
            <Icon name="arrow-undo-outline" size={16} color={theme.colors.secondary} />
            <Spacer.Horizontal size={4} />
            <OpenSans.Secondary size="s-16">{getTranslation('Undo rejection')}</OpenSans.Secondary>
          </Layout.PressableRow>
        </>
      ) : null}

      {approvalStatus === 'approved' ? (
        <Layout.Column px py bg="successXLight" border={[2, 'solid', 'success']} radius>
          <Layout.Row align>
            <Icon name="checkmark-circle" size={20} color={theme.colors.success} />
            <Spacer.Horizontal size={8} />
            <OpenSans.Primary weight="bold-700" style={{ color: theme.colors.successDark }}>
              {getTranslation('Approved')}
            </OpenSans.Primary>
          </Layout.Row>
          {approvalSource ? (
            <>
              <Spacer.Vertical size={4} />
              <OpenSans.Secondary size="xs-12">
                {getTranslation('Approved by')} {memberName(approvalSource.approvedBy)}{' '}
                {getTranslation('on')} {formatShortDate(approvalSource.approvedAt)}
              </OpenSans.Secondary>
            </>
          ) : null}
        </Layout.Column>
      ) : null}

      {approvalStatus === 'approved' && isManagerOrAdmin ? (
        <>
          <Spacer.Vertical size={8} />
          <Layout.PressableRow
            bg="transparent"
            onClick={() => runAction((cb) => undoExpenseApproval(expenseKey, cb))}
          >
            <Icon name="arrow-undo-outline" size={16} color={theme.colors.secondary} />
            <Spacer.Horizontal size={4} />
            <OpenSans.Secondary size="s-16">{getTranslation('Undo approval')}</OpenSans.Secondary>
          </Layout.PressableRow>
        </>
      ) : null}

      {approvalStatus === 'approved' && !isManagerOrAdmin ? (
        <>
          <Spacer.Vertical size={8} />
          <Layout.Row align>
            <Icon name="lock-closed-outline" size={14} color={theme.colors.secondary} />
            <Spacer.Horizontal size={4} />
            <OpenSans.Secondary size="xs-12">
              {getTranslation('Approved expenses are locked')}
            </OpenSans.Secondary>
          </Layout.Row>
        </>
      ) : null}

      {needsReimbursement ? (
        <>
          <Spacer.Vertical size={12} />
          <Layout.Column px py radius bg="warningXLight" border={[1, 'solid', 'warning']}>
            <Layout.Row align>
              <Icon name="cash-outline" size={20} color={theme.colors.warningDark} />
              <Spacer.Horizontal size={8} />
              <Layout.Column>
                <OpenSans.Primary weight="bold-700" style={{ color: theme.colors.warningDark }}>
                  {formatCurrency(Math.abs(total ?? 0))} {getTranslation('requires reimbursement')}
                </OpenSans.Primary>
                <OpenSans.Secondary size="xs-12">
                  {getTranslation('Owed to')} {memberName(createdBy)}
                  {paymentMethodValue?.value?.name ? ` · ${paymentMethodValue.value.name}` : ''}
                </OpenSans.Secondary>
              </Layout.Column>
            </Layout.Row>
            {isManagerOrAdmin ? (
              <>
                <Spacer.Vertical size={12} />
                <Button.Success
                  variant="bold"
                  py={12}
                  align="center"
                  style={{ width: '100%' }}
                  onClick={() => runAction((cb) => reimburseExpense(expenseKey, cb))}
                >
                  <Icon name="checkmark-outline" size={18} color={theme.colors.buttonWhite} />
                  <Spacer.Horizontal size={6} />
                  <OpenSans.Custom
                    size="m-18"
                    weight="bold-700"
                    style={{ color: theme.colors.buttonWhite }}
                  >
                    {getTranslation('Mark Reimbursed')}
                  </OpenSans.Custom>
                </Button.Success>
              </>
            ) : null}
          </Layout.Column>
        </>
      ) : null}

      {hasBeenReimbursed ? (
        <>
          <Spacer.Vertical size={12} />
          <Layout.Column px py bg="successXLight" border={[2, 'solid', 'success']} radius>
            <Layout.Row align>
              <Icon name="checkmark-circle" size={18} color={theme.colors.success} />
              <Spacer.Horizontal size={8} />
              <OpenSans.Primary weight="bold-700" style={{ color: theme.colors.successDark }}>
                {getTranslation('Has been reimbursed')}
              </OpenSans.Primary>
            </Layout.Row>
            {reimbursementSource ? (
              <>
                <Spacer.Vertical size={4} />
                <OpenSans.Secondary size="xs-12">
                  {getTranslation('Paid by')} {memberName(reimbursementSource.reimbursedBy)}{' '}
                  {getTranslation('on')} {formatShortDate(reimbursementSource.reimbursedAt)}
                </OpenSans.Secondary>
              </>
            ) : null}
          </Layout.Column>
          {isManagerOrAdmin ? (
            <>
              <Spacer.Vertical size={8} />
              <Layout.PressableRow
                bg="transparent"
                onClick={() => runAction((cb) => undoExpenseReimbursement(expenseKey, cb))}
              >
                <Icon name="arrow-undo-outline" size={16} color={theme.colors.secondary} />
                <Spacer.Horizontal size={4} />
                <OpenSans.Secondary size="s-16">
                  {getTranslation('Undo reimbursement')}
                </OpenSans.Secondary>
              </Layout.PressableRow>
            </>
          ) : null}
        </>
      ) : null}
    </Layout.Column>
  );
};
